import type { OpcionLetra, OtpChannel } from '@cessi/shared';
import type { BenchmarkPayload, ScorePayload } from './api';

/** v3: cuestionario v1.0/v1.4 (schemaVersion 4). Borradores v2 respondían otras preguntas. */
export const DRAFT_KEY = 'cessi-autoeval-draft-v3';
const DRAFT_KEYS_OBSOLETAS = ['cessi-autoeval-draft-v2'];
const ORG_SUGGEST_KEY = 'cessi-org-suggest-v1';

export type DraftState = {
  stepIndex: number;
  nombre: string;
  email: string;
  phone: string;
  otpChannel: OtpChannel;
  organizacion: string;
  website: string;
  linkedin: string;
  consentimiento: boolean;
  otpVerified: boolean;
  otpBypass: boolean;
  otp_token: string | null;
  draft_token: string | null;
  results_token: string | null;
  score: ScorePayload | null;
  benchmark: BenchmarkPayload | null;
  p0_1: string;
  p0_2: string;
  p0_3: string;
  p0_4: string;
  p0_5: string;
  p0_6: string;
  p0_7: string;
  p0_8: string;
  p0_9: string;
  p0_10: string;
  respuestas: Record<string, OpcionLetra | ''>;
};

export function emptyDraft(): DraftState {
  return {
    stepIndex: 0,
    nombre: '',
    email: '',
    phone: '',
    otpChannel: 'email',
    organizacion: '',
    website: '',
    linkedin: '',
    consentimiento: false,
    otpVerified: false,
    otpBypass: false,
    otp_token: null,
    draft_token: null,
    results_token: null,
    score: null,
    benchmark: null,
    p0_1: '',
    p0_2: '',
    p0_3: '',
    p0_4: '',
    p0_5: '',
    p0_6: '',
    p0_7: '',
    p0_8: '',
    p0_9: '',
    p0_10: '',
    respuestas: {},
  };
}

export function loadDraft(): DraftState {
  try {
    for (const k of DRAFT_KEYS_OBSOLETAS) localStorage.removeItem(k);
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return emptyDraft();
    return { ...emptyDraft(), ...(JSON.parse(raw) as Partial<DraftState>) };
  } catch {
    return emptyDraft();
  }
}

export function saveDraft(state: DraftState): void {
  localStorage.setItem(DRAFT_KEY, JSON.stringify(state));
}

export function clearDraft(): void {
  localStorage.removeItem(DRAFT_KEY);
}

export function loadOrgSuggestions(): string[] {
  try {
    const raw = localStorage.getItem(ORG_SUGGEST_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as string[];
    return Array.isArray(arr) ? arr.filter((s) => typeof s === 'string').slice(0, 12) : [];
  } catch {
    return [];
  }
}

export function rememberOrg(name: string): void {
  const n = name.trim();
  if (n.length < 2) return;
  const prev = loadOrgSuggestions().filter((x) => x.toLowerCase() !== n.toLowerCase());
  localStorage.setItem(ORG_SUGGEST_KEY, JSON.stringify([n, ...prev].slice(0, 12)));
}

/** Payload compatible con PATCH draft / submit parcial. */
export function draftToServerPayload(d: DraftState): Record<string, unknown> {
  return {
    schemaVersion: 4,
    nombre: d.nombre,
    email: d.email,
    organizacion: d.organizacion,
    website: d.website,
    linkedin: d.linkedin || undefined,
    consentimiento: d.consentimiento || undefined,
    p0_1: d.p0_1 || undefined,
    p0_2: d.p0_2 || undefined,
    p0_3: d.p0_3 || undefined,
    p0_4: d.p0_4 || undefined,
    p0_5: d.p0_5 || undefined,
    p0_6: d.p0_6 || undefined,
    p0_7: d.p0_7 || undefined,
    p0_8: d.p0_8 || undefined,
    p0_9: d.p0_9 || undefined,
    p0_10: d.p0_10 || undefined,
    respuestas: d.respuestas,
    stepIndex: d.stepIndex,
  };
}
